import type {
  MyNarrativeCompany,
  NarrativeCompanyDraftUpdate,
  NarrativeCompanyLatestPublishedUpdate,
} from '../../hooks/useNarrativeCompany';

const isLatestPublishedUpdateLocked = (
  latestPublishedUpdate: NarrativeCompanyLatestPublishedUpdate
) => {
  return (
    (latestPublishedUpdate?.lockedAt ? true : false) ||
    (latestPublishedUpdate?.willLockAt &&
      new Date().getTime() >=
        new Date(latestPublishedUpdate.willLockAt).getTime())
  );
};

const getInitialContent = (
  draftUpdate: NarrativeCompanyDraftUpdate,
  latestPublishedUpdate: NarrativeCompanyLatestPublishedUpdate
) => {
  if (
    latestPublishedUpdate &&
    !isLatestPublishedUpdateLocked(latestPublishedUpdate)
  ) {
    return latestPublishedUpdate.coreHtml ?? '';
  }

  return draftUpdate?.coreHtml ?? '';
};

const getInitialTitle = (
  draftUpdate: NarrativeCompanyDraftUpdate,
  latestPublishedUpdate: NarrativeCompanyLatestPublishedUpdate
) => {
  if (
    latestPublishedUpdate &&
    !isLatestPublishedUpdateLocked(latestPublishedUpdate)
  ) {
    return latestPublishedUpdate.title ?? '';
  }

  return draftUpdate?.title ?? '';
};

const getInitialUserHoldsPosition = (
  draftUpdate: NarrativeCompanyDraftUpdate,
  latestPublishedUpdate: NarrativeCompanyLatestPublishedUpdate
) => {
  if (
    latestPublishedUpdate &&
    !isLatestPublishedUpdateLocked(latestPublishedUpdate)
  ) {
    return latestPublishedUpdate.userHoldsPosition ?? false;
  }

  return draftUpdate?.userHoldsPosition ?? false;
};

const getInitialUpdateId = (
  latestPublishedUpdate: NarrativeCompanyLatestPublishedUpdate,
  draftUpdate: NarrativeCompanyDraftUpdate
) => {
  if (
    latestPublishedUpdate &&
    !isLatestPublishedUpdateLocked(latestPublishedUpdate)
  ) {
    return latestPublishedUpdate.id;
  }

  return draftUpdate?.id ?? null;
};

const getIsPublished = (
  myNarrative?: MyNarrativeCompany['Company']['myNarrative']
) => {
  if (myNarrative?.draftUpdate?.publishedAt) {
    return true;
  }

  if (myNarrative?.latestPublishedUpdate?.publishedAt) {
    return true;
  }

  return false;
};

const getIsPublishedLocked = (
  myNarrative?: MyNarrativeCompany['Company']['myNarrative']
) => {
  if (!myNarrative) {
    return false;
  }

  return (
    (getIsPublished(myNarrative) &&
      isLatestPublishedUpdateLocked(myNarrative?.latestPublishedUpdate)) ||
    false
  );
};

const getIsPublishedWithinGracePeriod = (
  myNarrative?: MyNarrativeCompany['Company']['myNarrative']
) => {
  if (!myNarrative) {
    return false;
  }

  return (
    getIsPublished(myNarrative) &&
    !isLatestPublishedUpdateLocked(myNarrative?.latestPublishedUpdate)
  );
};

export {
  getInitialContent,
  getInitialTitle,
  getInitialUpdateId,
  isLatestPublishedUpdateLocked,
  getIsPublishedLocked,
  getIsPublishedWithinGracePeriod,
  getInitialUserHoldsPosition,
};
